import {
  Box,
  CloseIcon,
  Drawer,
  IconButton,
  Link,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  NavigationIcon,
  Stack,
  Theme,
  useMediaQuery,
  useTheme
} from "@carrotfertility/carotene-core"
import { useCurrentUser } from "#/components/context/user/UserContext"
import { UserDropdown } from "#/features/global-ui/user-dropdown"
import React, { ReactElement } from "react"
import { useIntl } from "react-intl"
import { Link as RouterLink } from "react-router-dom"
import { useHasProviderFinder } from "./hooks/useHasProviderFinder"
import { MessagesNavItem } from "./MessagesNavItem"
import { getHelpCenterLink, goToHelpCenter } from "#/lib/carrot-api/HelpCenterClient"
import { FinancialSupportNavItem } from "./FinancialSupportNavItem"
import { ProviderFinderNavItem } from "./ProviderFinderNavItem"
import { TextDirection } from "#/types/Locale"
import { useTextDirection } from "#/services/locale/hooks/useTextDirection"
import useGetBenefit from "#/components/dynamic-content/hooks/useGetBenefit"
import { useFlags } from "launchdarkly-react-client-sdk"
import { MemberIdDialog } from "./MemberIdDialog"
import useCarrotPlan from "#/components/carrot-plans/hooks/useCarrotPlan"
import { useGetCarrotPlanRequestStatus } from "#/components/carrot-plans/hooks/useCarrotPlanRequestStatus"
import { useCalibratedEngagementEnabled } from "#/hooks/calibratedEngagement/useCalibratedEngagement"
import { CarrotCollectiveNavItem } from "#/components/views/app/navigation/CarrotCollectiveNavItem"
import { useIsSprintsVisible } from "#/services/user/hooks/useSprintsQuery"
import { Paths } from "#/utils/Paths"
import { FaqsNavItem } from "#/components/views/app/navigation/FaqsNavItem"
import { HeaderLanguageSelector } from "#/features/global-ui/language-selector"
import { ProgramType } from "#/lib/carrot-api/types/Benefit"

function getNavItemButtonStyles(theme: Theme, active?: boolean) {
  return {
    padding: theme.spacing(theme.tokens.spacing.md),
    background: active ? theme.palette.background.paper : null,
    color: theme.palette.text.secondary,
    ":focus, :hover": {
      background: theme.palette.background.paper,
      color: theme.palette.text.primary
    }
  }
}

export function NavItemWrapper({
  link,
  onClick,
  button = false,
  text,
  firstItem = false,
  extraNavIndicator
}: {
  link?: string
  onClick?: () => void
  button?: boolean
  firstItem?: boolean
  text: string
  extraNavIndicator?: ReactElement
}): JSX.Element {
  const linkProps = button ? {} : { component: RouterLink, to: link }
  const active = (() => {
    const pathnameMapping: Record<string, string> = {
      "/care-team": "/talk-to-carrot"
    }

    if (link && pathnameMapping[link] && window.location.pathname.startsWith(pathnameMapping[link])) {
      return true
    }

    return link === window.location.pathname || (link !== "/" && window.location.pathname.startsWith(link))
  })()
  return (
    <ListItem
      disablePadding
      sx={{ position: "relative", marginY: firstItem ? (theme) => theme.spacing(theme.tokens.spacing.lg) : "unset" }}
    >
      {active ? (
        <Box
          position="absolute"
          bgcolor={(theme) => theme.palette.primary.accent}
          width="6px"
          sx={{ inset: 0, zIndex: 2 }}
        />
      ) : null}
      <ListItemButton {...linkProps} sx={(theme) => getNavItemButtonStyles(theme, active)} onClick={onClick}>
        <ListItemText
          primaryTypographyProps={{
            color: "inherit",
            fontWeight: active ? 700 : 400
          }}
          primary={text}
        />
        {extraNavIndicator}
      </ListItemButton>
    </ListItem>
  )
}

function NavItemsList({ setDrawerOpen }: { setDrawerOpen: (drawerOpen: boolean) => void }): JSX.Element {
  const intl = useIntl()
  const closeDrawer = () => setDrawerOpen(false)
  const { isInAppLockdown, company } = useCurrentUser()
  const hasProviderFinder = useHasProviderFinder(company.countryCode)
  const benefitQuery = useGetBenefit()
  const isSupportCenterEnabled = benefitQuery?.data?.supportCenter?.enabled ?? false
  const carrotPlanQuery = useCarrotPlan()
  const cpRequestStatusQuery = useGetCarrotPlanRequestStatus()
  const calibratedEngagementEnabled = useCalibratedEngagementEnabled()
  const { carrotConnectEnabled, contentfulHelpCenter } = useFlags()
  const { isVisible: shouldShowSprintsPage } = useIsSprintsVisible()

  const hideForCalibratedEngagement =
    calibratedEngagementEnabled.data &&
    !carrotPlanQuery.hasCarrotPlan &&
    !cpRequestStatusQuery.data?.carrotPlanIsOnTheWay

  const isEngage = benefitQuery.data?.program?.type === ProgramType.WTW_ENGAGE

  const navItemsToShow: {
    component: ReactElement
    key: string
    showForCarrotLite: boolean
    isHidden?: boolean
  }[] = [
    {
      key: "nav-messages-link",
      showForCarrotLite: true,
      component: <MessagesNavItem onClick={closeDrawer} />
    },
    {
      key: "nav-library-link",
      showForCarrotLite: true,
      component: (
        <NavItemWrapper
          link={Paths.READ}
          onClick={closeDrawer}
          text={intl.formatMessage({ defaultMessage: "Carrot Academy" })}
        />
      )
    },
    {
      key: "nav-transactions-link",
      showForCarrotLite: false,
      isHidden: hideForCalibratedEngagement,
      component: <FinancialSupportNavItem onClick={closeDrawer} showLockedIcon={isInAppLockdown} />
    },
    {
      key: "nav-ttc-link",
      showForCarrotLite: true,
      component: (
        <NavItemWrapper
          link={Paths.CARE_TEAM}
          onClick={closeDrawer}
          text={intl.formatMessage({ defaultMessage: "Care Team" })}
        />
      )
    },
    {
      key: "nav-provider-finder-link",
      showForCarrotLite: true,
      isHidden: !hasProviderFinder || hideForCalibratedEngagement,
      component: <ProviderFinderNavItem onClick={closeDrawer} />
    },
    {
      key: "nav-your-benefit-guide-link",
      showForCarrotLite: true,
      component: (
        <NavItemWrapper
          link={Paths.YOUR_BENEFIT_GUIDE}
          onClick={closeDrawer}
          text={intl.formatMessage({ defaultMessage: "Your benefit guide" })}
        />
      )
    },
    {
      key: "nav-carrot-collective-link",
      showForCarrotLite: true,
      isHidden: isEngage,
      component: <CarrotCollectiveNavItem onClick={closeDrawer} />
    },
    {
      key: "nav-sprints-link",
      showForCarrotLite: true,
      isHidden: !shouldShowSprintsPage,
      component: (
        <NavItemWrapper
          link={Paths.SPRINTS_LANDING_PAGE}
          onClick={closeDrawer}
          text={intl.formatMessage({ defaultMessage: "Metabolic health support" })}
        />
      )
    },
    {
      key: "nav-support-center-link",
      showForCarrotLite: true,
      isHidden: !isSupportCenterEnabled,
      component: contentfulHelpCenter ? (
        <FaqsNavItem onClick={closeDrawer} />
      ) : (
        <ListItem
          disablePadding
          onClick={(event) => {
            goToHelpCenter(event)
            closeDrawer()
          }}
        >
          <ListItemButton
            sx={(theme) => getNavItemButtonStyles(theme)}
            component="a"
            href={getHelpCenterLink()}
            target="_blank"
          >
            <ListItemText
              primaryTypographyProps={{ color: "inherit" }}
              primary={intl.formatMessage({
                defaultMessage: "FAQs",
                description:
                  "Navigation bar label to route users to the Frequently Asked Questions page. Please do not translate the letters F-A-Q-S literally. Use the standard shorthand or acronym for 'Frequently Asked Questions' for the locale (e.g. the equivalent abbreviation that is used)."
              })}
            />
          </ListItemButton>
        </ListItem>
      )
    }
  ]
    .filter((ni) => (company.carrotLite && ni.showForCarrotLite) || !company.carrotLite)
    .filter(({ isHidden }) => !isHidden)

  return (
    <>
      {carrotConnectEnabled ? <MemberIdDialog /> : null}
      <List>
        <NavItemWrapper
          firstItem
          link={Paths.HOME}
          onClick={closeDrawer}
          text={intl.formatMessage({ defaultMessage: "Your Carrot Plan" })}
        />
        {navItemsToShow.map(({ component, key }) => React.cloneElement(component, { key }))}
      </List>
    </>
  )
}

function MobileNav({
  drawerOpen,
  setDrawerOpen
}: {
  drawerOpen: boolean
  setDrawerOpen: (drawerOpen: boolean) => void
}) {
  const intl = useIntl()
  const textDirection = useTextDirection()

  return (
    <>
      <Box
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        padding={(theme) => theme.tokens.spacing.md}
        bgcolor={(theme) => theme.palette.background.default}
        minHeight="96px"
        maxHeight="6rem"
      >
        <IconButton
          onClick={() => {
            setDrawerOpen(true)
          }}
        >
          <NavigationIcon />
        </IconButton>
        <RouterLink to="/">
          <Box
            component="img"
            src={`/images/CarrotGrey.svg`}
            aria-label={intl.formatMessage({ defaultMessage: "Go to homepage" })}
            height={(theme) => theme.spacing(theme.tokens.spacing.md)}
          />
        </RouterLink>

        <Stack direction="row" alignItems="center" justifyContent="flex-end">
          <HeaderLanguageSelector />
          <UserDropdown />
        </Stack>
      </Box>

      <Drawer
        variant={"temporary"}
        ModalProps={{
          keepMounted: true // Better open performance on mobile.
        }}
        sx={(theme) => ({
          display: "block",
          [theme.breakpoints.up("lg")]: { display: "none" },
          "& .MuiDrawer-paper": {
            background: theme.palette.background.default,
            inlineSize: "256px"
          }
        })}
        anchor={textDirection === TextDirection.LeftToRight ? "left" : "right"}
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
      >
        <Box
          sx={(theme) => ({
            display: "flex",
            justifyContent: "flex-end",
            paddingBlockStart: theme.spacing(theme.tokens.spacing.md),
            paddingBlockEnd: theme.spacing(theme.tokens.spacing.sm),
            paddingX: theme.spacing(theme.tokens.spacing.md)
          })}
        >
          <IconButton aria-label={intl.formatMessage({ defaultMessage: "close" })} onClick={() => setDrawerOpen(false)}>
            <CloseIcon />
          </IconButton>
        </Box>
        <NavItemsList {...{ setDrawerOpen }} />
      </Drawer>
    </>
  )
}

export function MainNavigation(): JSX.Element {
  const [drawerOpen, setDrawerOpen] = React.useState<boolean>(false)
  const theme = useTheme()
  const showMobileNav = useMediaQuery(theme.breakpoints.down("lg"))
  const intl = useIntl()
  const textDirection = useTextDirection()

  return (
    <>
      {showMobileNav ? (
        <MobileNav {...{ drawerOpen, setDrawerOpen }} />
      ) : (
        <Drawer
          variant="permanent"
          anchor={textDirection === TextDirection.LeftToRight ? "left" : "right"}
          sx={(theme) => ({
            // position added to ensure this drawer always appears behind the dynamic content tool or blocking modals
            position: "sticky",
            display: "block",
            [theme.breakpoints.down("lg")]: { display: "none" },
            "& .MuiDrawer-paper": {
              boxSizing: "border-box",
              background: theme.palette.background.navigation,
              minInlineSize: "256px",
              maxInlineSize: "16rem"
            }
          })}
        >
          <Box
            sx={(theme) => ({
              display: "flex",
              flexDirection: "column",
              marginBlockStart: theme.tokens.spacing.xxxl,
              marginInlineStart: theme.tokens.spacing.md,
              inlineSize: "130px"
            })}
          >
            <Link href="/" underline="none">
              <Box
                component="img"
                src={`/images/CarrotGrey.svg`}
                aria-label={intl.formatMessage({ defaultMessage: "Go to homepage" })}
                height={(theme) => theme.spacing(theme.tokens.spacing.lg)}
              />
            </Link>
          </Box>
          <NavItemsList {...{ setDrawerOpen }} />
        </Drawer>
      )}
    </>
  )
}
